import {useState, useEffect, useCallback, useRef} from 'react';

import {defineMessages, useIntl, FormattedMessage} from 'react-intl';
import styled from 'styled-components';

import {ICoach} from '@stryd/models';
import {
  DropdownMenu,
  FullPageSpinner,
  dimensions,
  colors,
  IDropdownMenuItem,
} from '@stryd/react-ui';
import {getPxValueFromString} from '@stryd/util-lib';

import {useApi} from 'src/contexts/ApiContext';
import {withBasePath} from 'src/utils/router';

import CoachesList from '../coaches-list/CoachesList';

import CoachesFilter from './CoachesFilter';

const CoachesPage = styled.div`
  background-color: ${colors.themes.dark.background};
  & button {
    cursor: pointer;
    outline: none;
  }
  @media screen and (max-width: ${dimensions.breakpoints.mobile}) {
    display: flex;
    flex-flow: column nowrap;
  }
  a {
    color: ${colors.themes.dark.textHighlight};
  }
`;

const CoachesListData = styled.div`
  display: flex;
  flex-flow: row wrap;
  align-items: center;
  justify-content: space-between;
  padding: 5px 40px 10px 40px;
`;

const ResultCount = styled.div`
  font-size: 16px;
  color: ${colors.themes.dark.text};
`;

const SortBar = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: center;
  color: ${colors.themes.dark.text};
  * {
    font-size: 16px;
  }
  & select {
    border: none;
    background: transparent;
    color: ${colors.themes.dark.text};
  }
  & select:focus {
    outline: none;
  }
  & select option {
    background-color: ${colors.themes.dark.backgroundInverted};
  }
  & span {
    margin-right: 10px;
  }
`;

const FilterWrapper = styled.div`
  width: 100%;
  position: sticky;
  top: ${dimensions.topNavHeight};
  z-index: 1;
  background: ${colors.themes.dark.background};
  @media screen and (max-width: ${dimensions.breakpoints.mobile}) {
    position: static;
  }
`;

const MainPageContent = styled.section`
  display: flex;
  flex-flow: row wrap;
  align-items: flex-start;
  justify-content: flex-start;
  background: ${colors.themes.dark.background};
`;

const CoachBanner = styled.div`
  width: 100%;
  height: 350px;
  position: relative;
`;

const BannerOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, #000000 0%, rgba(0, 0, 0, 0) 69.86%);
  display: flex;
  flex-flow: column nowrap;
  justify-content: center;
  align-items: flex-start;
  padding: 60px 120px;
  @media screen and (max-width: ${dimensions.breakpoints.desktopSmall}) {
    padding: 40px 80px;
  }
  @media screen and (max-width: ${dimensions.breakpoints.tabletLandscape}) {
    padding: 30px 60px;
  }
  @media screen and (max-width: ${dimensions.breakpoints.mobile}) {
    background: linear-gradient(90deg, #000000 0%, rgba(0, 0, 0, 0) 129%);
    padding: 0px 20px;
  }
`;

const BannerImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  @media screen and (max-width: ${dimensions.breakpoints.mobile}) {
    object-position: 65% 50%;
  }
`;

const BannerTitle = styled.h2`
  font-size: 2em;
  font-weight: normal;
  color: ${colors.themes.dark.buttonPrimaryText};
  margin-bottom: 25px;
  @media screen and (max-width: ${dimensions.breakpoints.desktopSmall}) {
    font-size: 1.7em;
  }
  @media screen and (max-width: ${dimensions.breakpoints.tabletLandscape}) {
    font-size: 1.5em;
  }
  @media screen and (max-width: ${dimensions.breakpoints.tabletPortrait}) {
    font-size: 1.7em;
    display: flex;
    flex-flow: column nowrap;
    align-items: flex-start;
    justify-content: center;
    & b {
      margin-top: 10px;
    }
  }
  @media screen and (max-width: ${dimensions.breakpoints.mobile}) {
    font-size: 1.5em;
    & b {
      line-height: 1.25em;
    }
  }
`;

const BannerDescription = styled.p`
  color: ${colors.themes.dark.buttonPrimaryText};
  font-size: 1.3em;
  margin-bottom: 0;
  &:last-of-type {
    margin-top: 15px;
    margin-bottom: 30px;
  }
  @media screen and (max-width: ${dimensions.breakpoints.desktopSmall}) {
    font-size: 1em;
  }
  @media screen and (max-width: ${dimensions.breakpoints.mobile}) {
    font-size: 0.9em;
  }
`;

const BannerDivider = styled.div`
  min-height: 2px;
  min-width: 70px;
  background-color: ${colors.themes.dark.textSecondary};
  margin: 20px 0;
`;

const BannerSubDescription = styled.p`
  color: ${colors.themes.dark.buttonPrimaryText};
  margin-bottom: 10px;
  font-size: 1em;
  &:last-of-type {
    margin-bottom: 0;
  }
  @media screen and (max-width: ${dimensions.breakpoints.desktopSmall}) {
    font-size: 0.85em;
  }
  @media screen and (max-width: ${dimensions.breakpoints.mobile}) {
    font-size: 0.8em;
  }
`;

const MessageWrapper = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: center;
  height: 350px;
  color: ${colors.themes.dark.text};
`;

const NoResults = styled.p`
  width: 50%;
  text-align: center;
  opacity: 1;
  color: ${colors.themes.dark.textSecondary};
  @media screen and (max-width: ${dimensions.breakpoints.mobile}) {
    width: 80%;
  }
`;

const sortOptionLabelDescriptors = defineMessages({
  aToZ: {
    id: 'coaches.sortOptions.aToZ',
    defaultMessage: 'Alphabetical (A to Z)',
  },
  zToA: {
    id: 'coaches.sortOptions.zToA',
    defaultMessage: 'Alphabetical (Z to A)',
  },
  random: {
    id: 'coaches.sortOptions.random',
    defaultMessage: 'Random',
  },
});

const sortOptions = {
  aToZ: {
    value: 'aToZ',
    labelMessageDescriptor: sortOptionLabelDescriptors.aToZ,
    sortFunction: (a: ICoach, b: ICoach) => {
      let name1 = '';
      if (a.prefix.length > 0) {
        name1 += a.prefix.toLowerCase() + ' ';
      }
      name1 += a.first_name.toLowerCase() + ' ';
      if (a.middle_name.length > 0) {
        name1 += a.middle_name.toLowerCase() + ' ';
      }
      name1 += a.last_name.toLowerCase();
      name1.replace(/[^a-zA-Z0-9]/g, '');

      let name2 = '';
      if (b.prefix.length > 0) {
        name2 += b.prefix.toLowerCase() + ' ';
      }
      name2 += b.first_name.toLowerCase() + ' ';
      if (b.middle_name.length > 0) {
        name2 += b.middle_name.toLowerCase() + ' ';
      }
      name2 += b.last_name.toLowerCase();
      name2.replace(/[^a-zA-Z0-9]/g, '');

      return name1 < name2 ? -1 : 1;
    },
  },
  zToA: {
    value: 'zToA',
    labelMessageDescriptor: sortOptionLabelDescriptors.zToA,
    sortFunction: (a: ICoach, b: ICoach) => {
      let name1 = '';
      if (a.prefix.length > 0) {
        name1 += a.prefix.toLowerCase() + ' ';
      }
      name1 += a.first_name.toLowerCase() + ' ';
      if (a.middle_name.length > 0) {
        name1 += a.middle_name.toLowerCase() + ' ';
      }
      name1 += a.last_name.toLowerCase();
      name1.replace(/[^a-zA-Z0-9]/g, '');

      let name2 = '';
      if (b.prefix.length > 0) {
        name2 += b.prefix.toLowerCase() + ' ';
      }
      name2 += b.first_name.toLowerCase() + ' ';
      if (b.middle_name.length > 0) {
        name2 += b.middle_name.toLowerCase() + ' ';
      }
      name2 += b.last_name.toLowerCase();
      name2.replace(/[^a-zA-Z0-9]/g, '');
      return name1 < name2 ? 1 : -1;
    },
  },
  random: {
    value: 'random',
    labelMessageDescriptor: sortOptionLabelDescriptors.random,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    sortFunction: (a: ICoach, b: ICoach) => {
      if (Math.round(Math.random())) {
        return 1;
      } else {
        return -1;
      }
    },
  },
};

export const CoachesPageTemplate: React.FC<
  React.PropsWithChildren<unknown>
> = () => {
  const api = useApi();
  const [apiResponse, setApiResponse] = useState(false);
  const [apiError, setApiError] = useState(false);
  const [data, setData] = useState<ICoach[]>([]);
  const [filteredData, setFilteredData] = useState<ICoach[]>([]);
  const [sortedData, setSortedData] = useState<ICoach[]>([]);
  const [sortMenuOpen, setSortMenuOpen] = useState(false);
  const [sortOption, setSortOption] = useState(sortOptions.random);
  const [filtersBottom, setFiltersBottom] = useState('0px');

  const filterWrapperRef = useRef<HTMLDivElement | null>(null);

  const intl = useIntl();

  useEffect(() => {
    const getCoaches = async (): Promise<void> => {
      const res = await api.coach.getCoaches();

      if (res.ok) {
        if (res.data === null) {
          setData([]);
        } else {
          setData(res.data);
          setFilteredData(res.data);
        }
      } else {
        setApiError(true);
      }
      setApiResponse(true);
    };

    getCoaches();
  }, [api.coach]);

  useEffect(() => {
    const offsetList = () => {
      if (filterWrapperRef.current) {
        const filterBounds = filterWrapperRef.current.getBoundingClientRect();

        if (
          window.innerWidth >
          parseInt(getPxValueFromString(dimensions.breakpoints.mobile))
        ) {
          setFiltersBottom(`${filterBounds.bottom}px`);
        } else {
          setFiltersBottom(`0px`);
        }
      }
    };

    offsetList();

    window.addEventListener('resize', offsetList);

    return function cleanup() {
      window.removeEventListener('resize', offsetList);
    };
  }, []);

  const handleFilterChange = useCallback(
    (filter: any) => setFilteredData(filter(data)),
    [data]
  );

  useEffect(() => {
    setSortedData([...filteredData].sort(sortOption.sortFunction));
  }, [filteredData, sortOption]);

  const updateSortOption = (menuItem: IDropdownMenuItem) => {
    const newOption = menuItem ? menuItem.id : null;
    switch (newOption) {
      case sortOptions.aToZ.value: {
        setSortOption(sortOptions.aToZ);
        break;
      }
      case sortOptions.zToA.value: {
        setSortOption(sortOptions.zToA);
        break;
      }
      case sortOptions.random.value: {
        setSortOption(sortOptions.random);
        break;
      }
      default: {
        throw new Error('Invalid sorting option!');
      }
    }
    setSortMenuOpen(false);
  };

  const menuItems = Object.values(sortOptions).map((option) => {
    return {
      id: option.value,
      label: intl.formatMessage(option.labelMessageDescriptor),
      onClick: updateSortOption,
    };
  });

  return (
    <CoachesPage>
      <FilterWrapper ref={filterWrapperRef}>
        <CoachesFilter onFilterChange={handleFilterChange} />
        <CoachesListData>
          <ResultCount>
            {!apiResponse ? (
              <FormattedMessage
                id="coaches.loadingResults"
                defaultMessage="Loading Results"
              />
            ) : (
              <FormattedMessage
                id="coaches.numResults"
                defaultMessage="{numResults, plural, one {# Result} other {# Results}}"
                values={{
                  numResults: sortedData.length,
                }}
              />
            )}
          </ResultCount>
          <SortBar>
            <span>
              <FormattedMessage
                id="coaches.sortLabel"
                defaultMessage="Sort: "
              />
            </span>
            <DropdownMenu
              buttonStyle={{fontSize: '16px'}}
              bodyStyle={{fontSize: '16px'}}
              menuItems={menuItems}
              isOpen={sortMenuOpen}
              onToggle={(open) => {
                setSortMenuOpen(open);
              }}
              selectedItemId={sortOption.value}
            />
          </SortBar>
        </CoachesListData>
      </FilterWrapper>
      <MainPageContent>
        <CoachBanner>
          <BannerImage src={withBasePath('/images/coaches-banner.jpeg')} />
          <BannerOverlay>
            <BannerTitle>
              <FormattedMessage
                id="coaches.heading"
                defaultMessage="Welcome to the <b>Stryd Power Coaching Page</b>"
                // eslint-disable-next-line react/display-name
                values={{b: (chunks: string) => <b>{chunks}</b>}}
              />
            </BannerTitle>
            <BannerDescription>
              <FormattedMessage
                id="coaches.description"
                defaultMessage="Stryd’s Power Coaching Group is home to the world’s best
               power-based running coaches."
              />
            </BannerDescription>
            <BannerDivider />
            <BannerSubDescription>
              <FormattedMessage
                id="coaches.instruction"
                defaultMessage="Explore the coaches below. Click on their profile to find their contact information to inquire about one-on-one coaching or power-based training plans."
              />
            </BannerSubDescription>
            <BannerSubDescription>
              <FormattedMessage
                id="coaches.joinMessage"
                defaultMessage="If you want to join the coaching program, <a>click here to learn about enrollment.</a>"
                values={{
                  // eslint-disable-next-line react/display-name
                  a: (chunks: string) => (
                    <a
                      href="https://stryd.teachable.com/p/stryd-for-coaches"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {chunks}
                    </a>
                  ),
                }}
              />
            </BannerSubDescription>
          </BannerOverlay>
        </CoachBanner>
        {!apiResponse && (
          <MessageWrapper>
            <FullPageSpinner
              zIndex={1}
              text={intl.formatMessage({
                id: 'coaches.loadingText',
                defaultMessage: 'Loading Coaches...',
              })}
            />
          </MessageWrapper>
        )}
        {sortedData && sortedData.length > 0 && (
          <CoachesList data={sortedData} stickAt={filtersBottom} />
        )}
        {((!apiError && apiResponse && data && data.length === 0) ||
          (!apiError &&
            apiResponse &&
            sortedData &&
            sortedData.length === 0)) && (
          <MessageWrapper>
            <NoResults>
              <FormattedMessage
                id="coaches.emptyResult"
                defaultMessage="No Results"
              />
            </NoResults>
          </MessageWrapper>
        )}
        {apiError && (
          <MessageWrapper>
            <NoResults>
              <FormattedMessage
                id="coaches.apiErrorMessage"
                defaultMessage="Oops! It looks like there is something wrong on our end. Please try again later!"
              />
            </NoResults>
          </MessageWrapper>
        )}
      </MainPageContent>
    </CoachesPage>
  );
};
